import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormControl, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Subject, Subscription, combineLatest, finalize, takeUntil } from "rxjs";
import { Functionality } from "src/app/shared/constants/functionality.constants";
import { PermissionService } from "src/app/shared/services/permission.service";
import {
  DiabetesType,
  IAfterSurgery,
  IBeforeSurgery,
  IDiagnostic,
  IMedical,
  IMetadataOptions,
  IMetastasis,
  IMutation,
  ISampleMetadata,
  ISampleMetadataModificationDetails,
  IStaging,
  ISymptomatologyAfterSurgery,
  ITumorCharacteristicsAndHistology,
  Sex,
} from "../../domain/interfaces/metadata.interface";
import { IPredictionCheckResult } from "../../domain/interfaces/predict.interface";
import { MetadataService } from "../../domain/services/metadata.service";
import { PredictService } from "../../domain/services/predict.service";
import { tumorHistologyValidator } from "../../domain/validators/tumor-histology.validator";

@Component({
  selector: "app-metadata-page",
  templateUrl: "./metadata-page.component.html",
  styleUrls: ["./metadata-page.component.scss"],
})
export class MetadataPageComponent implements OnInit, OnDestroy {
  readonly medicalFields = [
    "colonPolyp",
    "familialAdenomatousPolyp",
    "lynchSyndrome",
    "breastCancerHist",
    "ovarianCancerHist",
    "uterineCancerHist",
    "inflammatoryBowelDisease",
    "rcuhCrohn",
  ] as const satisfies readonly Exclude<keyof IMedical, "diabetes" | "diabetesType">[];
  readonly symptomatologyAfterSurgeryFields = [
    "diarrhea",
    "constipation",
    "bloodInStool",
    "rectalBleeding",
    "abdominalCramp",
    "rectalPain",
    "lumpInAbdomen",
    "weaknessAndFatigue",
    "lossAppetite",
    "intestinalObstruction",
    "swollenLiver",
    "swollenLymphNodes",
    "jaundice",
    "ascites",
  ] as const satisfies readonly (keyof ISymptomatologyAfterSurgery)[];
  readonly stagingFields = ["ptnmT", "ptnmN", "ptnmM"] as const satisfies readonly Exclude<keyof IStaging, "pathologicalStage">[];
  readonly histologyFields = [
    { key: "histology", type: "select", required: true },
    { key: "histologyType", type: "text", required: false },
    { key: "lymphoma", type: "boolean", required: false },
    { key: "lymphomaType", type: "text", required: false },
    { key: "gist", type: "boolean", required: false },
    { key: "gistType", type: "text", required: false },
    { key: "carcino", type: "boolean", required: false },
  ] as const satisfies readonly {
    key: keyof ITumorCharacteristicsAndHistology;
    type: "boolean" | "text" | "select";
    required: boolean;
  }[];
  readonly diagnosticFields = ["colonoscopy", "biopsy", "coloCt", "rectalExam"] as const satisfies readonly (keyof IDiagnostic)[];
  readonly metastasisFields = [
    "liver",
    "lung",
    "brain",
    "bone",
    "lymphNode",
    "peritoneum",
    "pancreas",
    "kidney",
    "adrenal",
  ] as const satisfies readonly (keyof IMetastasis)[];
  readonly mutationFields = ["apc", "kras", "braf", "nras", "mmr", "mlh1", "pms2"] as const satisfies readonly Exclude<
    keyof IMutation,
    "msiStatus"
  >[];
  readonly beforeSurgeryFields = [
    { key: "neoChemo", type: "boolean", required: true },
    { key: "combChemo", type: "text", required: false },
    { key: "radio", type: "boolean", required: true },
    { key: "immunotherapy", type: "text", required: false },
  ] as const satisfies readonly {
    key: keyof IBeforeSurgery;
    type: "boolean" | "text";
    required: boolean;
  }[];
  readonly afterSurgeryFields = [
    { key: "adjuvantChemotherapy", type: "text", required: true },
    { key: "combChemo", type: "text", required: false },
    { key: "radio", type: "boolean", required: false },
    { key: "immunotherapy", type: "text", required: false },
  ] as const satisfies readonly {
    key: keyof IAfterSurgery;
    type: "boolean" | "text";
    required: boolean;
  }[];
  booleanOptions = [
    { value: true, label: "common.yes", translate: true },
    { value: false, label: "common.no", translate: true },
  ];
  booleanOrNullOptions = [...this.booleanOptions, { value: null, label: "common.unknown", translate: true }];
  sexOptions = [
    { value: Sex.FEMALE, label: "metadataPage.metadata.demographic.sex.female", translate: true },
    { value: Sex.MALE, label: "metadataPage.metadata.demographic.sex.male", translate: true },
  ];
  diabetesTypeOptions = [
    { value: DiabetesType.NONE, label1: "common.no", translate1: true },
    { value: DiabetesType.PRE_DIABETES, label1: "common.yes", label2: "Pre diabetes", translate1: true, translate2: false },
    { value: DiabetesType.TYPE1, label1: "common.yes", label2: "Type 1", translate1: true, translate2: false },
    { value: DiabetesType.TYPE2, label1: "common.yes", label2: "Type 2", translate1: true, translate2: false },
    { value: DiabetesType.UNKNOWN, label1: "common.yes", label2: "common.unknown", translate1: true, translate2: true },
  ];
  diabetesTypeOrNullOptions = [...this.diabetesTypeOptions, { value: null, label1: "common.unknown", translate1: true }];
  // Allowed values of the fields constrained to a fixed value set, filled by loadMetadataOptions()
  metadataOptions: IMetadataOptions | null = null;
  locationOptions: { value: string; label: string; translate: boolean }[] = [];
  histologyOptions: { value: string; label: string; translate: boolean }[] = [];
  msiStatusOptions: { value: string; label: string; translate: boolean }[] = [];
  msiStatusOrNullOptions: { value: string | null; label: string; translate: boolean }[] = [];
  pathologicalStageOptions: { value: string; label: string; translate: boolean }[] = [];
  pathologicalStageOrNullOptions: { value: string | null; label: string; translate: boolean }[] = [];

  postgreSQLId!: number;

  sampleMetadata!: ISampleMetadata;

  public dots = Array(5); // Animation loader
  public isLoadingMetadata = true;
  public isLoadingCheck = true;
  public metadataLoadFailed = false;
  public checkLoadFailed = false;
  public isLoadingOptions = true;
  public optionsLoadFailed = false;
  public showMissing = true;
  public showShap = true;
  public check!: IPredictionCheckResult;
  public threshold = 0.5;

  private onDestroy$ = new Subject<void>();
  // In-flight loads, cancelled when a newer one starts so that a late response can't overwrite it
  private metadataSubscription?: Subscription;
  private checkSubscription?: Subscription;

  editMode = false;
  public metadataForm = this.fb.group({
    metadata: this.fb.group({
      name: [""],
      firstname: [""],
      demographic: this.fb.group({
        age: new FormControl<number>(0, { nonNullable: true, validators: Validators.required }),
        bmi: [0],
        sex: new FormControl<Sex>(Sex.FEMALE, { nonNullable: true, validators: Validators.required }),
        smokeTobacco: [false],
      }),
      medical: this.fb.group({
        breastCancerHist: [false],
        colonPolyp: [false],
        diabetesType: new FormControl<DiabetesType | null>(DiabetesType.NONE),
        familialAdenomatousPolyp: [false],
        inflammatoryBowelDisease: [false],
        lynchSyndrome: [false],
        ovarianCancerHist: [false],
        rcuhCrohn: [false],
        uterineCancerHist: [false],
      }),
      symptomatologyAfterSurgery: this.fb.group({
        abdominalCramp: [false],
        ascites: [false],
        bloodInStool: [false],
        constipation: [false],
        diarrhea: [false],
        intestinalObstruction: [false],
        jaundice: [false],
        lossAppetite: [false],
        lumpInAbdomen: [false],
        rectalBleeding: [false],
        rectalPain: [false],
        swollenLiver: [false],
        swollenLymphNodes: [false],
        weaknessAndFatigue: [false],
      }),
    }),
    tumor: this.fb.group({
      mrnaSeqID: [""],
      sampleType: new FormControl<string>("", { nonNullable: true, validators: Validators.required }),
      differentiationDegree: [0],
      diagnostic: this.fb.group({
        biopsy: [false],
        coloCt: [false],
        colonoscopy: [false],
        rectalExam: [false],
      }),
      metastasis: this.fb.group({
        adrenal: [false],
        bone: [false],
        brain: [false],
        breast: [false],
        kidney: [false],
        liver: [false],
        lung: [false],
        lymphNode: [false],
        pancreas: [false],
        peritoneum: [false],
      }),
      mutation: this.fb.group({
        apc: [false],
        braf: [false],
        kras: [false],
        mlh1: [false],
        mmr: [false],
        msiStatus: new FormControl<string | null>(null),
        nras: [false],
        pms2: [false],
      }),
      staging: this.fb.nonNullable.group({
        ptnmT: ["", Validators.required],
        ptnmN: ["", Validators.required],
        ptnmM: ["", Validators.required],
        pathologicalStage: new FormControl<string | null>(null),
      }),
      stroma: this.fb.group({
        inflammatoryStroma: [false],
      }),
      tumorCharacteristicsAndHistology: this.fb.group(
        {
          histology: new FormControl<string>("", { nonNullable: true, validators: Validators.required }),
          histologyType: [""],
          carcino: [false],
          gist: [false],
          gistType: [""],
          location: new FormControl<string>("", { nonNullable: true, validators: Validators.required }),
          lymphoma: [false],
          lymphomaType: [""],
        },
        {
          validators: [tumorHistologyValidator("gist", "gistType"), tumorHistologyValidator("lymphoma", "lymphomaType")],
        }
      ),
    }),
    treatment: this.fb.group({
      surgery: [""],
      description: [""],
      beforeSurgery: this.fb.group({
        neoChemo: new FormControl<boolean>(false, { nonNullable: true, validators: [Validators.required] }),
        combChemo: [""],
        radio: new FormControl<boolean>(false, { nonNullable: true, validators: [Validators.required] }),
        immunotherapy: [""],
      }),
      afterSurgery: this.fb.group({
        adjuvantChemotherapy: new FormControl<string>("", { nonNullable: true, validators: [Validators.required] }),
        combChemo: [""],
        radio: new FormControl<boolean | null>(null),
        immunotherapy: [""],
      }),
    }),
  });

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private metadataService: MetadataService,
    private predictService: PredictService,
    public snackBar: MatSnackBar,
    private translateService: TranslateService,
    private router: Router,
    private permissionService: PermissionService
  ) {}

  /**
   * Loads metadata, check, dropdown options and initializes the form.
   */
  ngOnInit(): void {
    // The options are only used in edit mode, so they're only fetched if the role allows modifying data
    if (this.canModifyData) {
      this.loadMetadataOptions();
    } else {
      this.isLoadingOptions = false;
    }

    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(([params, queryParams]) => {
        const id = Number(params.get("id"));

        if (!id) {
          this.router.navigate(["not-found"]);
          return;
        }

        this.postgreSQLId = id;
        // A previous failure doesn't concern this load, and must not prevent editing below
        this.metadataLoadFailed = false;
        // Only honored if editing is possible, as the edit button itself is hidden otherwise
        this.editMode = queryParams.get("edit") === "true" && this.canEdit;

        this.loadMetadata();
        this.loadCheck();
      });
  }

  /**
   * Emits onDestroy$ to terminate all active subscriptions.
   */
  ngOnDestroy(): void {
    this.metadataSubscription?.unsubscribe();
    this.checkSubscription?.unsubscribe();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Loads sample metadata
   */
  private loadMetadata(): void {
    this.isLoadingMetadata = true;

    this.metadataSubscription?.unsubscribe();
    this.metadataSubscription = this.metadataService.getSampleMetadata(this.postgreSQLId).subscribe({
      next: (data) => {
        this.sampleMetadata = data;
        this.buildForm();
        this.isLoadingMetadata = false;
      },
      error: (err) => {
        this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
          duration: 5000,
        });
        this.isLoadingMetadata = false;
        // Only a missing sample leads to not-found, any other error is shown on the page
        if (err.status === 404) {
          this.router.navigate(["not-found"]);
        } else {
          this.metadataLoadFailed = true;
          this.editMode = false;
        }
      },
    });
  }

  /**
   * Loads check information.
   * A failure keeps the user on the page, as the sample itself exists: the check is flagged as unavailable rather than
   * displayed, as a previous check may no longer match the saved metadata.
   */
  private loadCheck(): void {
    this.isLoadingCheck = true;
    this.checkLoadFailed = false;
    this.checkSubscription?.unsubscribe();
    this.checkSubscription = this.predictService.checkPrediction(this.postgreSQLId).subscribe({
      next: (check) => {
        this.check = check;
        this.isLoadingCheck = false;
      },
      error: () => {
        this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
          duration: 5000,
        });
        this.checkLoadFailed = true;
        this.isLoadingCheck = false;
      },
    });
  }

  /**
   * Loads the allowed values of the fields constrained to a fixed value set, offered as dropdowns in edit mode
   */
  private loadMetadataOptions(): void {
    this.isLoadingOptions = true;
    this.metadataService.getMetadataOptions().subscribe({
      next: (options) => {
        this.metadataOptions = options;
        this.locationOptions = options.location.map((location) => ({ value: location, label: location, translate: false }));
        this.histologyOptions = options.histology.map((histology) => this.toHistologyOption(histology));
        this.msiStatusOptions = options.msiStatus.map((msiStatus) => ({ value: msiStatus, label: msiStatus, translate: false }));
        this.msiStatusOrNullOptions = [...this.msiStatusOptions, { value: null, label: "common.unknown", translate: true }];
        this.pathologicalStageOptions = options.pathologicalStage.map((stage) => ({ value: stage, label: stage, translate: false }));
        this.pathologicalStageOrNullOptions = [...this.pathologicalStageOptions, { value: null, label: "common.unknown", translate: true }];
        this.isLoadingOptions = false;
      },
      error: () => {
        // Without the options, the dropdowns would be empty, so editing is disabled
        this.snackBar.open(`❗ ${this.translateService.instant("metadataPage.error.optionsLoad")}`, "OK", {
          duration: 5000,
        });
        this.optionsLoadFailed = true;
        this.editMode = false;
        this.isLoadingOptions = false;
      },
    });
  }

  /**
   * Run prediction for the current sample.
   */
  public predict(): void {
    this.predictService.predict(this.postgreSQLId).subscribe({
      next: (prediction) => {
        this.check = {
          ready: false,
          model: prediction.model,
          prediction: {
            model: prediction.model,
            sensitivityProbability: prediction.probaSensitivity,
            shapValues: prediction.shapValues,
            baseValue: prediction.baseValue,
          },
        };
      },
      error: (err) => {
        if (err.status === 400) {
          this.snackBar.open(`❗ ${this.translateService.instant("common.errorInvalidFormat")}`, "OK", {
            duration: 5000,
          });
        } else if (err.status === 409) {
          // It means the prediction was asked in another tab, so it is available
          this.check.prediction = err.error;
        } else if (err.status === 404) {
          this.check = err.error;
        }
      },
    });
  }

  /**
   * Whether the user's role allows modifying a sample's metadata
   */
  get canModifyData(): boolean {
    return this.permissionService.isEnabled(Functionality.MODIFY_DATA);
  }

  /**
   * Whether the BMI must stay filled in: it is once the sample has one, as the API can't clear it
   */
  get bmiRequired(): boolean {
    return this.sampleMetadata.metadata.demographic.bmi !== null;
  }

  /**
   * Whether the differentiation degree must stay filled in: it is once the sample has one, as the API can't clear it
   */
  get differentiationDegreeRequired(): boolean {
    return this.sampleMetadata.tumor.differentiationDegree !== null;
  }

  /**
   * Whether the sample's metadata can be edited: the role must allow it, and both the metadata and the dropdown options
   * must have loaded
   */
  get canEdit(): boolean {
    return this.canModifyData && !this.metadataLoadFailed && !this.optionsLoadFailed;
  }

  /**
   * Whether the user's role allows running a prediction on the sample
   */
  get canPredict(): boolean {
    return this.predictService.canPredict();
  }

  /**
   * Toggles edit mode for the metadata form.
   */
  toggleEdit(): void {
    this.editMode = !this.editMode;
  }

  /**
   * Cancels editing and restores the original metadata values.
   */
  cancelEdit(): void {
    this.buildForm();
    this.toggleEdit();
  }

  /**
   * Populates the form with sample metadata values.
   */
  private buildForm(): void {
    if (!this.sampleMetadata) return;
    this.metadataForm.controls.metadata.controls.name.setValue(this.sampleMetadata.metadata.name);
    this.metadataForm.controls.metadata.controls.firstname.setValue(this.sampleMetadata.metadata.firstname);
    this.metadataForm.controls.metadata.controls.demographic.controls.age.setValue(this.sampleMetadata.metadata.demographic.age);
    // The API can't clear a BMI once filled in, so it can't be emptied then
    this.metadataForm.controls.metadata.controls.demographic.controls.bmi.setValidators(this.bmiRequired ? Validators.required : null);
    this.metadataForm.controls.metadata.controls.demographic.controls.bmi.setValue(this.sampleMetadata.metadata.demographic.bmi);
    this.metadataForm.controls.metadata.controls.demographic.controls.sex.setValue(this.sampleMetadata.metadata.demographic.sex);
    this.metadataForm.controls.metadata.controls.demographic.controls.smokeTobacco.setValue(
      this.sampleMetadata.metadata.demographic.smokeTobacco
    );

    this.metadataForm.controls.metadata.controls.medical.controls.breastCancerHist.setValue(
      this.sampleMetadata.metadata.medical.breastCancerHist
    );
    this.metadataForm.controls.metadata.controls.medical.controls.colonPolyp.setValue(this.sampleMetadata.metadata.medical.colonPolyp);
    this.metadataForm.controls.metadata.controls.medical.controls.diabetesType.setValue(this.sampleMetadata.metadata.medical.diabetesType);
    this.metadataForm.controls.metadata.controls.medical.controls.familialAdenomatousPolyp.setValue(
      this.sampleMetadata.metadata.medical.familialAdenomatousPolyp
    );
    this.metadataForm.controls.metadata.controls.medical.controls.inflammatoryBowelDisease.setValue(
      this.sampleMetadata.metadata.medical.inflammatoryBowelDisease
    );
    this.metadataForm.controls.metadata.controls.medical.controls.lynchSyndrome.setValue(
      this.sampleMetadata.metadata.medical.lynchSyndrome
    );
    this.metadataForm.controls.metadata.controls.medical.controls.ovarianCancerHist.setValue(
      this.sampleMetadata.metadata.medical.ovarianCancerHist
    );
    this.metadataForm.controls.metadata.controls.medical.controls.rcuhCrohn.setValue(this.sampleMetadata.metadata.medical.rcuhCrohn);
    this.metadataForm.controls.metadata.controls.medical.controls.uterineCancerHist.setValue(
      this.sampleMetadata.metadata.medical.uterineCancerHist
    );

    this.metadataForm.controls.metadata.controls.symptomatologyAfterSurgery.controls.abdominalCramp.setValue(
      this.sampleMetadata.metadata.symptomatologyAfterSurgery.abdominalCramp
    );
    this.metadataForm.controls.metadata.controls.symptomatologyAfterSurgery.controls.ascites.setValue(
      this.sampleMetadata.metadata.symptomatologyAfterSurgery.ascites
    );
    this.metadataForm.controls.metadata.controls.symptomatologyAfterSurgery.controls.bloodInStool.setValue(
      this.sampleMetadata.metadata.symptomatologyAfterSurgery.bloodInStool
    );
    this.metadataForm.controls.metadata.controls.symptomatologyAfterSurgery.controls.constipation.setValue(
      this.sampleMetadata.metadata.symptomatologyAfterSurgery.constipation
    );
    this.metadataForm.controls.metadata.controls.symptomatologyAfterSurgery.controls.diarrhea.setValue(
      this.sampleMetadata.metadata.symptomatologyAfterSurgery.diarrhea
    );
    this.metadataForm.controls.metadata.controls.symptomatologyAfterSurgery.controls.intestinalObstruction.setValue(
      this.sampleMetadata.metadata.symptomatologyAfterSurgery.intestinalObstruction
    );
    this.metadataForm.controls.metadata.controls.symptomatologyAfterSurgery.controls.jaundice.setValue(
      this.sampleMetadata.metadata.symptomatologyAfterSurgery.jaundice
    );
    this.metadataForm.controls.metadata.controls.symptomatologyAfterSurgery.controls.lossAppetite.setValue(
      this.sampleMetadata.metadata.symptomatologyAfterSurgery.lossAppetite
    );
    this.metadataForm.controls.metadata.controls.symptomatologyAfterSurgery.controls.lumpInAbdomen.setValue(
      this.sampleMetadata.metadata.symptomatologyAfterSurgery.lumpInAbdomen
    );
    this.metadataForm.controls.metadata.controls.symptomatologyAfterSurgery.controls.rectalBleeding.setValue(
      this.sampleMetadata.metadata.symptomatologyAfterSurgery.rectalBleeding
    );
    this.metadataForm.controls.metadata.controls.symptomatologyAfterSurgery.controls.rectalPain.setValue(
      this.sampleMetadata.metadata.symptomatologyAfterSurgery.rectalPain
    );
    this.metadataForm.controls.metadata.controls.symptomatologyAfterSurgery.controls.swollenLiver.setValue(
      this.sampleMetadata.metadata.symptomatologyAfterSurgery.swollenLiver
    );
    this.metadataForm.controls.metadata.controls.symptomatologyAfterSurgery.controls.swollenLymphNodes.setValue(
      this.sampleMetadata.metadata.symptomatologyAfterSurgery.swollenLymphNodes
    );
    this.metadataForm.controls.metadata.controls.symptomatologyAfterSurgery.controls.weaknessAndFatigue.setValue(
      this.sampleMetadata.metadata.symptomatologyAfterSurgery.weaknessAndFatigue
    );

    this.metadataForm.controls.tumor.controls.mrnaSeqID.setValue(this.sampleMetadata.tumor.mrnaSeqID);
    this.metadataForm.controls.tumor.controls.sampleType.setValue(this.sampleMetadata.tumor.sampleType);
    // The API can't clear a differentiation degree once filled in, so it can't be emptied then
    this.metadataForm.controls.tumor.controls.differentiationDegree.setValidators(
      this.differentiationDegreeRequired ? Validators.required : null
    );
    this.metadataForm.controls.tumor.controls.differentiationDegree.setValue(this.sampleMetadata.tumor.differentiationDegree);

    this.metadataForm.controls.tumor.controls.diagnostic.controls.biopsy.setValue(this.sampleMetadata.tumor.diagnostic.biopsy);
    this.metadataForm.controls.tumor.controls.diagnostic.controls.coloCt.setValue(this.sampleMetadata.tumor.diagnostic.coloCt);
    this.metadataForm.controls.tumor.controls.diagnostic.controls.colonoscopy.setValue(this.sampleMetadata.tumor.diagnostic.colonoscopy);
    this.metadataForm.controls.tumor.controls.diagnostic.controls.rectalExam.setValue(this.sampleMetadata.tumor.diagnostic.rectalExam);

    this.metadataForm.controls.tumor.controls.metastasis.controls.adrenal.setValue(this.sampleMetadata.tumor.metastasis.adrenal);
    this.metadataForm.controls.tumor.controls.metastasis.controls.bone.setValue(this.sampleMetadata.tumor.metastasis.bone);
    this.metadataForm.controls.tumor.controls.metastasis.controls.brain.setValue(this.sampleMetadata.tumor.metastasis.brain);
    this.metadataForm.controls.tumor.controls.metastasis.controls.breast.setValue(this.sampleMetadata.tumor.metastasis.breast);
    this.metadataForm.controls.tumor.controls.metastasis.controls.kidney.setValue(this.sampleMetadata.tumor.metastasis.kidney);
    this.metadataForm.controls.tumor.controls.metastasis.controls.liver.setValue(this.sampleMetadata.tumor.metastasis.liver);
    this.metadataForm.controls.tumor.controls.metastasis.controls.lung.setValue(this.sampleMetadata.tumor.metastasis.lung);
    this.metadataForm.controls.tumor.controls.metastasis.controls.lymphNode.setValue(this.sampleMetadata.tumor.metastasis.lymphNode);
    this.metadataForm.controls.tumor.controls.metastasis.controls.pancreas.setValue(this.sampleMetadata.tumor.metastasis.pancreas);
    this.metadataForm.controls.tumor.controls.metastasis.controls.peritoneum.setValue(this.sampleMetadata.tumor.metastasis.peritoneum);

    this.metadataForm.controls.tumor.controls.mutation.controls.apc.setValue(this.sampleMetadata.tumor.mutation.apc);
    this.metadataForm.controls.tumor.controls.mutation.controls.braf.setValue(this.sampleMetadata.tumor.mutation.braf);
    this.metadataForm.controls.tumor.controls.mutation.controls.kras.setValue(this.sampleMetadata.tumor.mutation.kras);
    this.metadataForm.controls.tumor.controls.mutation.controls.mlh1.setValue(this.sampleMetadata.tumor.mutation.mlh1);
    this.metadataForm.controls.tumor.controls.mutation.controls.mmr.setValue(this.sampleMetadata.tumor.mutation.mmr);
    this.metadataForm.controls.tumor.controls.mutation.controls.msiStatus.setValue(this.sampleMetadata.tumor.mutation.msiStatus);
    this.metadataForm.controls.tumor.controls.mutation.controls.nras.setValue(this.sampleMetadata.tumor.mutation.nras);
    this.metadataForm.controls.tumor.controls.mutation.controls.pms2.setValue(this.sampleMetadata.tumor.mutation.pms2);

    this.metadataForm.controls.tumor.controls.staging.controls.ptnmT.setValue(this.sampleMetadata.tumor.staging.ptnmT);
    this.metadataForm.controls.tumor.controls.staging.controls.ptnmN.setValue(this.sampleMetadata.tumor.staging.ptnmN);
    this.metadataForm.controls.tumor.controls.staging.controls.ptnmM.setValue(this.sampleMetadata.tumor.staging.ptnmM);
    this.metadataForm.controls.tumor.controls.staging.controls.pathologicalStage.setValue(
      this.sampleMetadata.tumor.staging.pathologicalStage
    );

    this.metadataForm.controls.tumor.controls.stroma.controls.inflammatoryStroma.setValue(
      this.sampleMetadata.tumor.stroma.inflammatoryStroma
    );

    this.metadataForm.controls.tumor.controls.tumorCharacteristicsAndHistology.controls.histology.setValue(
      this.sampleMetadata.tumor.tumorCharacteristicsAndHistology.histology
    );
    this.metadataForm.controls.tumor.controls.tumorCharacteristicsAndHistology.controls.histologyType.setValue(
      this.sampleMetadata.tumor.tumorCharacteristicsAndHistology.histologyType
    );
    this.metadataForm.controls.tumor.controls.tumorCharacteristicsAndHistology.controls.carcino.setValue(
      this.sampleMetadata.tumor.tumorCharacteristicsAndHistology.carcino
    );
    this.metadataForm.controls.tumor.controls.tumorCharacteristicsAndHistology.controls.gist.setValue(
      this.sampleMetadata.tumor.tumorCharacteristicsAndHistology.gist
    );
    this.metadataForm.controls.tumor.controls.tumorCharacteristicsAndHistology.controls.gistType.setValue(
      this.sampleMetadata.tumor.tumorCharacteristicsAndHistology.gistType
    );
    this.metadataForm.controls.tumor.controls.tumorCharacteristicsAndHistology.controls.location.setValue(
      this.sampleMetadata.tumor.tumorCharacteristicsAndHistology.location
    );
    this.metadataForm.controls.tumor.controls.tumorCharacteristicsAndHistology.controls.lymphoma.setValue(
      this.sampleMetadata.tumor.tumorCharacteristicsAndHistology.lymphoma
    );
    this.metadataForm.controls.tumor.controls.tumorCharacteristicsAndHistology.controls.lymphomaType.setValue(
      this.sampleMetadata.tumor.tumorCharacteristicsAndHistology.lymphomaType
    );

    this.metadataForm.controls.treatment.controls.surgery.setValue(this.sampleMetadata.treatment.surgery);
    this.metadataForm.controls.treatment.controls.description.setValue(this.sampleMetadata.treatment.description);

    this.metadataForm.controls.treatment.controls.beforeSurgery.controls.neoChemo.setValue(
      this.sampleMetadata.treatment.beforeSurgery.neoChemo
    );
    this.metadataForm.controls.treatment.controls.beforeSurgery.controls.combChemo.setValue(
      this.sampleMetadata.treatment.beforeSurgery.combChemo
    );
    this.metadataForm.controls.treatment.controls.beforeSurgery.controls.radio.setValue(this.sampleMetadata.treatment.beforeSurgery.radio);
    this.metadataForm.controls.treatment.controls.beforeSurgery.controls.immunotherapy.setValue(
      this.sampleMetadata.treatment.beforeSurgery.immunotherapy
    );

    this.metadataForm.controls.treatment.controls.afterSurgery.controls.adjuvantChemotherapy.setValue(
      this.sampleMetadata.treatment.afterSurgery.adjuvantChemotherapy
    );
    this.metadataForm.controls.treatment.controls.afterSurgery.controls.combChemo.setValue(
      this.sampleMetadata.treatment.afterSurgery.combChemo
    );
    this.metadataForm.controls.treatment.controls.afterSurgery.controls.radio.setValue(this.sampleMetadata.treatment.afterSurgery.radio);
    this.metadataForm.controls.treatment.controls.afterSurgery.controls.immunotherapy.setValue(
      this.sampleMetadata.treatment.afterSurgery.immunotherapy
    );
  }

  /**
   * Saves metadata modifications and sends them to the API.
   */
  saveEdit(): void {
    if (this.metadataForm.invalid) {
      this.metadataForm.markAllAsTouched();
      return;
    }
    this.metadataForm.disable();

    const formValue = this.metadataForm.getRawValue();
    // Only the fields the user changed are sent, the others as null, so that saving doesn't overwrite
    // what another user changed meanwhile on fields left untouched here
    const diabetesType = this.changedOrNull(formValue.metadata.medical.diabetesType, this.sampleMetadata.metadata.medical.diabetesType);
    const sample: ISampleMetadataModificationDetails = {
      personal: {
        name: this.toClearableText(formValue.metadata.name, this.sampleMetadata.metadata.name),
        firstname: this.toClearableText(formValue.metadata.firstname, this.sampleMetadata.metadata.firstname),
        demographic: {
          age: this.changedOrNull(formValue.metadata.demographic.age, this.sampleMetadata.metadata.demographic.age),
          bmi: this.changedOrNull(formValue.metadata.demographic.bmi, this.sampleMetadata.metadata.demographic.bmi),
          sex: this.changedOrNull(formValue.metadata.demographic.sex, this.sampleMetadata.metadata.demographic.sex),
          smokeTobacco: this.changedOrNull(
            formValue.metadata.demographic.smokeTobacco,
            this.sampleMetadata.metadata.demographic.smokeTobacco
          ),
        },
        medical: {
          breastCancerHist: this.changedOrNull(
            formValue.metadata.medical.breastCancerHist,
            this.sampleMetadata.metadata.medical.breastCancerHist
          ),
          colonPolyp: this.changedOrNull(formValue.metadata.medical.colonPolyp, this.sampleMetadata.metadata.medical.colonPolyp),
          diabetes: diabetesType === null ? null : diabetesType !== DiabetesType.NONE,
          diabetesType: diabetesType,
          familialAdenomatousPolyp: this.changedOrNull(
            formValue.metadata.medical.familialAdenomatousPolyp,
            this.sampleMetadata.metadata.medical.familialAdenomatousPolyp
          ),
          inflammatoryBowelDisease: this.changedOrNull(
            formValue.metadata.medical.inflammatoryBowelDisease,
            this.sampleMetadata.metadata.medical.inflammatoryBowelDisease
          ),
          lynchSyndrome: this.changedOrNull(formValue.metadata.medical.lynchSyndrome, this.sampleMetadata.metadata.medical.lynchSyndrome),
          ovarianCancerHist: this.changedOrNull(
            formValue.metadata.medical.ovarianCancerHist,
            this.sampleMetadata.metadata.medical.ovarianCancerHist
          ),
          rcuhCrohn: this.changedOrNull(formValue.metadata.medical.rcuhCrohn, this.sampleMetadata.metadata.medical.rcuhCrohn),
          uterineCancerHist: this.changedOrNull(
            formValue.metadata.medical.uterineCancerHist,
            this.sampleMetadata.metadata.medical.uterineCancerHist
          ),
        },
        symptomatologyAfterSurgery: {
          abdominalCramp: this.changedOrNull(
            formValue.metadata.symptomatologyAfterSurgery.abdominalCramp,
            this.sampleMetadata.metadata.symptomatologyAfterSurgery.abdominalCramp
          ),
          ascites: this.changedOrNull(
            formValue.metadata.symptomatologyAfterSurgery.ascites,
            this.sampleMetadata.metadata.symptomatologyAfterSurgery.ascites
          ),
          bloodInStool: this.changedOrNull(
            formValue.metadata.symptomatologyAfterSurgery.bloodInStool,
            this.sampleMetadata.metadata.symptomatologyAfterSurgery.bloodInStool
          ),
          constipation: this.changedOrNull(
            formValue.metadata.symptomatologyAfterSurgery.constipation,
            this.sampleMetadata.metadata.symptomatologyAfterSurgery.constipation
          ),
          diarrhea: this.changedOrNull(
            formValue.metadata.symptomatologyAfterSurgery.diarrhea,
            this.sampleMetadata.metadata.symptomatologyAfterSurgery.diarrhea
          ),
          intestinalObstruction: this.changedOrNull(
            formValue.metadata.symptomatologyAfterSurgery.intestinalObstruction,
            this.sampleMetadata.metadata.symptomatologyAfterSurgery.intestinalObstruction
          ),
          jaundice: this.changedOrNull(
            formValue.metadata.symptomatologyAfterSurgery.jaundice,
            this.sampleMetadata.metadata.symptomatologyAfterSurgery.jaundice
          ),
          lossAppetite: this.changedOrNull(
            formValue.metadata.symptomatologyAfterSurgery.lossAppetite,
            this.sampleMetadata.metadata.symptomatologyAfterSurgery.lossAppetite
          ),
          lumpInAbdomen: this.changedOrNull(
            formValue.metadata.symptomatologyAfterSurgery.lumpInAbdomen,
            this.sampleMetadata.metadata.symptomatologyAfterSurgery.lumpInAbdomen
          ),
          rectalBleeding: this.changedOrNull(
            formValue.metadata.symptomatologyAfterSurgery.rectalBleeding,
            this.sampleMetadata.metadata.symptomatologyAfterSurgery.rectalBleeding
          ),
          rectalPain: this.changedOrNull(
            formValue.metadata.symptomatologyAfterSurgery.rectalPain,
            this.sampleMetadata.metadata.symptomatologyAfterSurgery.rectalPain
          ),
          swollenLiver: this.changedOrNull(
            formValue.metadata.symptomatologyAfterSurgery.swollenLiver,
            this.sampleMetadata.metadata.symptomatologyAfterSurgery.swollenLiver
          ),
          swollenLymphNodes: this.changedOrNull(
            formValue.metadata.symptomatologyAfterSurgery.swollenLymphNodes,
            this.sampleMetadata.metadata.symptomatologyAfterSurgery.swollenLymphNodes
          ),
          weaknessAndFatigue: this.changedOrNull(
            formValue.metadata.symptomatologyAfterSurgery.weaknessAndFatigue,
            this.sampleMetadata.metadata.symptomatologyAfterSurgery.weaknessAndFatigue
          ),
        },
      },
      tumor: {
        mrnaSeqID: this.toClearableText(formValue.tumor.mrnaSeqID, this.sampleMetadata.tumor.mrnaSeqID),
        sampleType: this.changedOrNull(formValue.tumor.sampleType, this.sampleMetadata.tumor.sampleType),
        differentiationDegree: this.changedOrNull(formValue.tumor.differentiationDegree, this.sampleMetadata.tumor.differentiationDegree),
        diagnostic: {
          biopsy: this.changedOrNull(formValue.tumor.diagnostic.biopsy, this.sampleMetadata.tumor.diagnostic.biopsy),
          coloCt: this.changedOrNull(formValue.tumor.diagnostic.coloCt, this.sampleMetadata.tumor.diagnostic.coloCt),
          colonoscopy: this.changedOrNull(formValue.tumor.diagnostic.colonoscopy, this.sampleMetadata.tumor.diagnostic.colonoscopy),
          rectalExam: this.changedOrNull(formValue.tumor.diagnostic.rectalExam, this.sampleMetadata.tumor.diagnostic.rectalExam),
        },
        metastasis: {
          adrenal: this.changedOrNull(formValue.tumor.metastasis.adrenal, this.sampleMetadata.tumor.metastasis.adrenal),
          bone: this.changedOrNull(formValue.tumor.metastasis.bone, this.sampleMetadata.tumor.metastasis.bone),
          brain: this.changedOrNull(formValue.tumor.metastasis.brain, this.sampleMetadata.tumor.metastasis.brain),
          breast: this.changedOrNull(formValue.tumor.metastasis.breast, this.sampleMetadata.tumor.metastasis.breast),
          kidney: this.changedOrNull(formValue.tumor.metastasis.kidney, this.sampleMetadata.tumor.metastasis.kidney),
          liver: this.changedOrNull(formValue.tumor.metastasis.liver, this.sampleMetadata.tumor.metastasis.liver),
          lung: this.changedOrNull(formValue.tumor.metastasis.lung, this.sampleMetadata.tumor.metastasis.lung),
          lymphNode: this.changedOrNull(formValue.tumor.metastasis.lymphNode, this.sampleMetadata.tumor.metastasis.lymphNode),
          pancreas: this.changedOrNull(formValue.tumor.metastasis.pancreas, this.sampleMetadata.tumor.metastasis.pancreas),
          peritoneum: this.changedOrNull(formValue.tumor.metastasis.peritoneum, this.sampleMetadata.tumor.metastasis.peritoneum),
        },
        mutation: {
          apc: this.changedOrNull(formValue.tumor.mutation.apc, this.sampleMetadata.tumor.mutation.apc),
          braf: this.changedOrNull(formValue.tumor.mutation.braf, this.sampleMetadata.tumor.mutation.braf),
          kras: this.changedOrNull(formValue.tumor.mutation.kras, this.sampleMetadata.tumor.mutation.kras),
          mlh1: this.changedOrNull(formValue.tumor.mutation.mlh1, this.sampleMetadata.tumor.mutation.mlh1),
          mmr: this.changedOrNull(formValue.tumor.mutation.mmr, this.sampleMetadata.tumor.mutation.mmr),
          nras: this.changedOrNull(formValue.tumor.mutation.nras, this.sampleMetadata.tumor.mutation.nras),
          pms2: this.changedOrNull(formValue.tumor.mutation.pms2, this.sampleMetadata.tumor.mutation.pms2),
          msiStatus: this.changedOrNull(formValue.tumor.mutation.msiStatus, this.sampleMetadata.tumor.mutation.msiStatus),
        },
        sequencing: {
          // Not editable on this page
          sequencingNgs: null,
        },
        staging: {
          ptnmT: this.changedOrNull(formValue.tumor.staging.ptnmT, this.sampleMetadata.tumor.staging.ptnmT),
          ptnmN: this.changedOrNull(formValue.tumor.staging.ptnmN, this.sampleMetadata.tumor.staging.ptnmN),
          ptnmM: this.changedOrNull(formValue.tumor.staging.ptnmM, this.sampleMetadata.tumor.staging.ptnmM),
          pathologicalStage: this.changedOrNull(
            formValue.tumor.staging.pathologicalStage,
            this.sampleMetadata.tumor.staging.pathologicalStage
          ),
        },
        stroma: {
          inflammatoryStroma: this.changedOrNull(
            formValue.tumor.stroma.inflammatoryStroma,
            this.sampleMetadata.tumor.stroma.inflammatoryStroma
          ),
        },
        tumorCharacteristicsAndHistology: {
          histology: this.changedOrNull(
            formValue.tumor.tumorCharacteristicsAndHistology.histology,
            this.sampleMetadata.tumor.tumorCharacteristicsAndHistology.histology
          ),
          histologyType: this.toClearableText(
            formValue.tumor.tumorCharacteristicsAndHistology.histologyType,
            this.sampleMetadata.tumor.tumorCharacteristicsAndHistology.histologyType
          ),
          carcino: this.changedOrNull(
            formValue.tumor.tumorCharacteristicsAndHistology.carcino,
            this.sampleMetadata.tumor.tumorCharacteristicsAndHistology.carcino
          ),
          gist: this.changedOrNull(
            formValue.tumor.tumorCharacteristicsAndHistology.gist,
            this.sampleMetadata.tumor.tumorCharacteristicsAndHistology.gist
          ),
          gistType: this.toClearableText(
            formValue.tumor.tumorCharacteristicsAndHistology.gistType,
            this.sampleMetadata.tumor.tumorCharacteristicsAndHistology.gistType
          ),
          location: this.changedOrNull(
            formValue.tumor.tumorCharacteristicsAndHistology.location,
            this.sampleMetadata.tumor.tumorCharacteristicsAndHistology.location
          ),
          lymphoma: this.changedOrNull(
            formValue.tumor.tumorCharacteristicsAndHistology.lymphoma,
            this.sampleMetadata.tumor.tumorCharacteristicsAndHistology.lymphoma
          ),
          lymphomaType: this.toClearableText(
            formValue.tumor.tumorCharacteristicsAndHistology.lymphomaType,
            this.sampleMetadata.tumor.tumorCharacteristicsAndHistology.lymphomaType
          ),
        },
      },
      treatment: {
        surgery: this.toClearableText(formValue.treatment.surgery, this.sampleMetadata.treatment.surgery),
        description: this.toClearableText(formValue.treatment.description, this.sampleMetadata.treatment.description),
        beforeSurgery: {
          neoChemo: this.changedOrNull(formValue.treatment.beforeSurgery.neoChemo, this.sampleMetadata.treatment.beforeSurgery.neoChemo),
          combChemo: this.toClearableText(
            formValue.treatment.beforeSurgery.combChemo,
            this.sampleMetadata.treatment.beforeSurgery.combChemo
          ),
          radio: this.changedOrNull(formValue.treatment.beforeSurgery.radio, this.sampleMetadata.treatment.beforeSurgery.radio),
          immunotherapy: this.toClearableText(
            formValue.treatment.beforeSurgery.immunotherapy,
            this.sampleMetadata.treatment.beforeSurgery.immunotherapy
          ),
        },
        afterSurgery: {
          adjuvantChemotherapy: this.changedOrNull(
            formValue.treatment.afterSurgery.adjuvantChemotherapy,
            this.sampleMetadata.treatment.afterSurgery.adjuvantChemotherapy
          ),
          combChemo: this.toClearableText(formValue.treatment.afterSurgery.combChemo, this.sampleMetadata.treatment.afterSurgery.combChemo),
          radio: this.changedOrNull(formValue.treatment.afterSurgery.radio, this.sampleMetadata.treatment.afterSurgery.radio),
          immunotherapy: this.toClearableText(
            formValue.treatment.afterSurgery.immunotherapy,
            this.sampleMetadata.treatment.afterSurgery.immunotherapy
          ),
        },
      },
    };

    this.metadataService
      .updateSampleMetadata(this.postgreSQLId, sample)
      .pipe(finalize(() => this.metadataForm.enable()))
      .subscribe({
        next: (metadata) => {
          this.sampleMetadata = metadata;
          this.metadataForm.reset();
          this.buildForm();
          this.snackBar.open("Metadata updated", "OK", { duration: 5000 });
          this.editMode = false;
          // The changes may complete or invalidate the data required to predict
          this.loadCheck();
        },
        error: (err) => {
          if (err.status === 400) {
            this.snackBar.open(`❗ ${this.translateService.instant("common.errorInvalidFormat")}`, "OK", {
              duration: 5000,
            });
          } else if (err.status === 403) {
            this.snackBar.open(`❗ ${this.translateService.instant("common.errorNotAllowed")}`, "OK", {
              duration: 5000,
            });
          } else if (err.status === 404) {
            this.snackBar.open(`❗ ${this.translateService.instant("metadataPage.error.404")}`, "OK", {
              duration: 5000,
            });
            this.router.navigate(["not-found"]);
          }
        },
      });
  }

  /**
   * The sample's histology as an option, to display it the same way as in the dropdown
   */
  get histologyOption(): { value: string; label: string; translate: boolean } {
    return this.toHistologyOption(this.sampleMetadata.tumor.tumorCharacteristicsAndHistology.histology);
  }

  /**
   * Converts a histology code into a dropdown option, translating the "OTHER" code.
   * @param histology Histology code
   * @return The option for this histology
   */
  private toHistologyOption(histology: string): { value: string; label: string; translate: boolean } {
    if (histology === "OTHER") return { value: histology, label: "common.other", translate: true };
    return { value: histology, label: histology, translate: false };
  }

  /**
   * Returns the value to send for a field: null when left unchanged, the API then keeping the stored value.
   * @param value Current form value
   * @param previousValue Value the sample had before editing
   * @return The changed value, or null if unchanged
   */
  private changedOrNull<T>(value: T | null, previousValue: T | null): T | null {
    return value === previousValue ? null : value;
  }

  /**
   * Returns the value to send for a free-text field the user can clear: null when left unchanged, like changedOrNull().
   * The API ignores a null value, so an emptied field that had a value is sent as "" to actually clear it.
   * An empty field can be null or "" depending on its input, and the API may already hold "" (from uploads),
   * so both count as the same empty value.
   * @param value Current form value
   * @param previousValue Value the sample had before editing
   * @return The changed value ("" to clear it), or null if unchanged
   */
  private toClearableText(value: string | null, previousValue: string | null): string | null {
    const current = value || null;
    if (current === (previousValue || null)) return null;
    return current ?? "";
  }

  /**
   * Returns the boolean options to display, including the "unknown" option when the current value is null.
   * @param value Current field value
   * @return List of boolean options to offer
   */
  getBooleanOptions(value: boolean | null): {
    value: boolean | null;
    label: string;
    translate: boolean;
  }[] {
    return value === null ? this.booleanOrNullOptions : this.booleanOptions;
  }

  /**
   * Returns the MSI status options to display, including the "unknown" option only when the current value is null,
   * as the API can't clear an MSI status once filled in.
   * @param value Current field value
   * @return List of MSI status options to offer
   */
  getMsiStatusOptions(value: string | null): { value: string | null; label: string; translate: boolean }[] {
    return value === null ? this.msiStatusOrNullOptions : this.msiStatusOptions;
  }

  /**
   * Returns the pathological stage options to display, including the "unknown" option only when the current value is null,
   * as the API can't clear a pathological stage once filled in.
   * @param value Current field value
   * @return List of pathological stage options to offer
   */
  getPathologicalStageOptions(value: string | null): { value: string | null; label: string; translate: boolean }[] {
    return value === null ? this.pathologicalStageOrNullOptions : this.pathologicalStageOptions;
  }

  /**
   * Returns the diabetes type options to display, including the "unknown" option when the current value is null.
   * @param value Current field value
   * @return List of diabetes type options to offer
   */
  getDiabetesOptions(value: boolean | null): (
    | {
        value: DiabetesType | null;
        label1: string;
        translate1: boolean;
        label2?: undefined;
        translate2?: undefined;
      }
    | {
        value: DiabetesType;
        label1: string;
        label2: string;
        translate1: boolean;
        translate2: boolean;
      }
  )[] {
    return value === null ? this.diabetesTypeOrNullOptions : this.diabetesTypeOptions;
  }
}
